import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { VariableBenefitPaymentChangeSubmission } from '../../../../../../models/submissions/variable-benefit-payment-change-submission';
import { VariableBenefitPaymentChangeSubmissionData } from '../../../../../../models/submissions/variable-benefit-payment-change-submission-data';
import { VariableBenefitDestinationJurisdictionDetail } from '../../../../../../models/submissions/variable-benefit-destination-jurisdiction-detail';
import { TrackByUtils } from '../../../../../../utils/track-by-utils';
import { DateConversionUtils } from '../../../../../../utils/date-conversion-utils';
import { currentVbPlanYear } from '../../../variable-benefit-accounts/utils/vb-annual-details-utils';

interface MinAgeRow {
  jurisdictionName: string;
  personName: string;
  isSpouse: boolean;
  age: number;
  dateOfBirth: string;
}

interface MinAgeComparisonRow {
  jurisdictionKey: string;
  jurisdictionName: string;
  current: MinAgeRow;
  new: MinAgeRow;
  changed: boolean;
}

@Component({
  selector: 'app-update-vb-payments-min-age-review-card',
  templateUrl: './update-vb-payments-min-age-review-card.component.html',
  styleUrls: ['./update-vb-payments-min-age-review-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UpdateVbPaymentsMinAgeReviewCardComponent implements OnChanges {
  @Input({ required: true }) submission!: VariableBenefitPaymentChangeSubmission;

  public isUnchanged = true;
  public comparisonRows: MinAgeComparisonRow[] = [];
  public effectiveDate = this.startOfNextCalendarYear();
  public trackByIndex = TrackByUtils.trackByIndex;

  public ngOnChanges(): void {
    const previous = this.submission?.previous ?? null;
    const newData = this.submission?.new ?? null;

    // This year's election is shown at the age it was worked out on this year, and the new one at the age it will be
    // worked out on when it takes effect next year
    const previousByKey = this.toRowsByKey(previous, currentVbPlanYear());
    const newByKey = this.toRowsByKey(newData, this.effectiveDate.getFullYear());
    const allKeys = new Set<string>([...previousByKey.keys(), ...newByKey.keys()]);

    const rows = Array.from(allKeys)
      .map(key => {
        const previousRow = previousByKey.get(key) ?? null;
        const newRow = newByKey.get(key) ?? null;
        const current = previousRow ?? newRow!;
        const next = newRow ?? previousRow!;
        return {
          jurisdictionKey: key,
          jurisdictionName: next.jurisdictionName,
          current,
          new: next,
          changed: previousRow !== null && newRow !== null && this.rowsDiffer(previousRow, newRow)
        };
      })
      .sort((a, b) => {
        if (!a.jurisdictionName) return 1;
        if (!b.jurisdictionName) return -1;
        return a.jurisdictionName.localeCompare(b.jurisdictionName);
      });

    this.comparisonRows = rows;
    this.isUnchanged = previous === null || newData === null || rows.every(row => !row.changed);
  }

  private toRowsByKey(data: VariableBenefitPaymentChangeSubmissionData | null, year: number): Map<string, MinAgeRow> {
    const details = data?.jurisdictionDetails ?? [];
    const entries = details
      .filter(detail => !!detail.minimumAgeDateOfBirth)
      .map((detail): [string, MinAgeRow] => [this.jurisdictionKey(detail), this.toRow(detail, year)]);
    return new Map(entries);
  }

  private toRow(detail: VariableBenefitDestinationJurisdictionDetail, year: number): MinAgeRow {
    return {
      jurisdictionName: detail.governingJurisdiction?.name ?? '',
      personName: detail.minimumAgeName,
      isSpouse: detail.minimumAgeSpouseId !== null,
      age: DateConversionUtils.calculateAgeOnJanuaryFirst(detail.minimumAgeDateOfBirth, year),
      dateOfBirth: detail.minimumAgeDateOfBirth
    };
  }

  private rowsDiffer(previous: MinAgeRow, next: MinAgeRow): boolean {
    return (
      previous.personName !== next.personName ||
      previous.dateOfBirth !== next.dateOfBirth ||
      previous.isSpouse !== next.isSpouse
    );
  }

  private jurisdictionKey(detail: VariableBenefitDestinationJurisdictionDetail): string {
    return detail.governingJurisdictionId !== null ? String(detail.governingJurisdictionId) : 'non-locked-in';
  }

  private startOfNextCalendarYear(): Date {
    return new Date(new Date().getFullYear() + 1, 0, 1);
  }
}
